import { Alert, Anchor, Button, PasswordInput, Stack, TextInput } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { authApi } from '../api/auth';
import { errorMessage } from '../api/client';
import { emptyToNull, registerSchema, type RegisterValues } from '../auth/schemas';
import { AuthCard } from '../components/AuthCard';
import { ru } from '../i18n/ru';
import { accentText } from '../theme';

export function RegisterPage() {
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      lastName: '',
      firstName: '',
      middleName: '',
      phone: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      const result = await authApi.register({
        email: values.email,
        password: values.password,
        lastName: values.lastName.trim(),
        firstName: values.firstName.trim(),
        middleName: emptyToNull(values.middleName),
        phone: emptyToNull(values.phone),
      });
      setDone(result.message);
    } catch (error) {
      setFailure(errorMessage(error, 'Не удалось зарегистрироваться.'));
    }
  });

  if (done !== null) {
    return (
      <AuthCard title={ru.auth.registerTitle} width={480}>
        <Stack gap="md">
          <Alert color="green" title={ru.common.success}>
            {done}
          </Alert>
          <Anchor component={Link} to="/login" size="sm" c={accentText}>
            {ru.auth.goToLogin}
          </Anchor>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={ru.auth.registerTitle} description={ru.auth.registerHint} width={480}>
      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Stack gap="md">
          {failure !== null && (
            <Alert color="red" title={ru.common.error}>
              {failure}
            </Alert>
          )}

          <TextInput
            label={ru.fields.lastName}
            autoComplete="family-name"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
          <TextInput
            label={ru.fields.firstName}
            autoComplete="given-name"
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <TextInput
            label={ru.fields.middleName + ru.fields.optional}
            error={errors.middleName?.message}
            {...register('middleName')}
          />
          <TextInput
            label={ru.fields.phone + ru.fields.optional}
            type="tel"
            autoComplete="tel"
            placeholder="+7 900 123-45-67"
            error={errors.phone?.message}
            {...register('phone')}
          />
          <TextInput
            label={ru.fields.email}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
          <PasswordInput
            label={ru.fields.password}
            autoComplete="new-password"
            description={ru.auth.passwordRule}
            error={errors.password?.message}
            {...register('password')}
          />

          <Button type="submit" loading={isSubmitting} mt="xs">
            {ru.auth.registerSubmit}
          </Button>

          <Anchor component={Link} to="/login" size="sm" c={accentText}>
            {ru.auth.hasAccount}
          </Anchor>
        </Stack>
      </form>
    </AuthCard>
  );
}
